/**
 * Access / multi-product gate configurator — types & defaults.
 * Completely separate from the sectional garage-door configurator.
 */

export type AccessCategoryId =
  | "residential_garage"
  | "industrial"
  | "roller"
  | "swing"
  | "loading_dock"
  | "high_speed"
  | "barrier";

/**
 * Category id stored in older Access settings. `"sliding"` (Posuvná brána) was
 * replaced by `loading_dock`. Parsers map it; the picker no longer offers it.
 */
export type StoredAccessCategoryId = AccessCategoryId | "sliding";

export type AccessPreviewMode = "layered" | "photo_stack" | "variant_swap" | "layer_stack";

/** MakroPro panel motif: g grooved, n flat, w micro-rib */
export type IndustrialMotif = "g" | "n" | "w";

export type IndustrialSurface = "woodgrain" | "smoothgrain";

/** BR-100 cassette: box = with cassette, nobox = bare render */
export type RollerCassette = "box" | "nobox";

export const INDUSTRIAL_MOTIFS: IndustrialMotif[] = ["g", "n", "w"];
export const INDUSTRIAL_SURFACES: IndustrialSurface[] = ["woodgrain", "smoothgrain"];
export const ROLLER_CASSETTES: RollerCassette[] = ["box", "nobox"];

export const DEFAULT_INDUSTRIAL_MOTIF: IndustrialMotif = "g";
export const DEFAULT_INDUSTRIAL_SURFACE: IndustrialSurface = "woodgrain";
export const DEFAULT_ROLLER_CASSETTE: RollerCassette = "box";

export type AccessGateVariant = "sectional" | "tilt" | "single_leaf" | "double_leaf" | "standard";

export type AccessAutomationMount = "surface" | "articulated" | "underground";

/** Admin / stored multilingual label (hu/sk/en). */
export interface AccessLocalizedName {
  hu: string;
  sk: string;
  en: string;
}

export interface AccessColorOption {
  code: string;
  /** Plain string (legacy) or localized object from the palette admin. */
  name: string | AccessLocalizedName;
  colorHex: string;
  /** Overlay JPEG filename under `…/overlays/` (optional; falls back to path convention). */
  overlayFilename?: string;
}

/** Motif / cassette / similar named option with optional base PNG. */
export interface AccessNamedOption {
  id: string;
  name: AccessLocalizedName;
  baseFilename?: string;
}

/** Industrial surface group with its own colour list. */
export interface AccessSurfaceGroup {
  id: string;
  name: AccessLocalizedName;
  colors: AccessColorOption[];
}

/**
 * Editable industrial + roller photo catalogue stored in
 * `settings.accessConfiguratorOptions.photoCatalog`.
 */
export interface AccessPhotoCatalogStored {
  industrial: {
    motifs: AccessNamedOption[];
    surfaces: AccessSurfaceGroup[];
  };
  roller: {
    cassettes: AccessNamedOption[];
    colors: AccessColorOption[];
  };
}

export function emptyLocalizedName(): AccessLocalizedName {
  return { hu: "", sk: "", en: "" };
}

export function localizeName(
  name: string | AccessLocalizedName | undefined | null,
  fallback = ""
): AccessLocalizedName {
  if (name && typeof name === "object" && ("hu" in name || "sk" in name || "en" in name)) {
    return {
      hu: String(name.hu ?? ""),
      sk: String(name.sk ?? ""),
      en: String(name.en ?? ""),
    };
  }
  const s = typeof name === "string" && name.trim() ? name.trim() : fallback;
  return { hu: s, sk: s, en: s };
}

/** Pick hu/sk/en from a localized (or plain) name for the active UI language. */
export function pickAccessLocalizedName(
  name: string | AccessLocalizedName | undefined | null,
  lang: string,
  fallback = ""
): string {
  const loc = localizeName(name, fallback);
  const key: keyof AccessLocalizedName = lang.toLowerCase().startsWith("hu")
    ? "hu"
    : lang.toLowerCase().startsWith("en")
      ? "en"
      : "sk";
  return (loc[key] || loc.sk || loc.hu || loc.en || fallback).trim();
}

export function accessColorDisplayName(
  color: AccessColorOption | undefined | null,
  lang: string,
  fallback = ""
): string {
  if (!color) return fallback;
  return pickAccessLocalizedName(color.name, lang, fallback || color.code);
}

export interface AccessCategoryDef {
  id: AccessCategoryId;
  /** CRM product_types.name (HU canonical) */
  productType: string;
  previewMode: AccessPreviewMode;
  /** Show Roger automation block */
  hasAutomation: boolean;
  /** May call TPT price calculator when sectional residential */
  pricingCapable: boolean;
}

export const ACCESS_CATEGORIES: AccessCategoryDef[] = [
  {
    id: "residential_garage",
    productType: "Garázskapu",
    previewMode: "layered",
    hasAutomation: true,
    pricingCapable: true,
  },
  {
    id: "industrial",
    productType: "Ipari kapu",
    previewMode: "photo_stack",
    hasAutomation: true,
    pricingCapable: false,
  },
  {
    id: "roller",
    productType: "Redőnykapu",
    previewMode: "photo_stack",
    hasAutomation: true,
    pricingCapable: false,
  },
  {
    id: "swing",
    /** CRM name stays the seeded product type; the UI calls it a swing garage door. */
    productType: "Szárnyas kapu",
    previewMode: "photo_stack",
    hasAutomation: true,
    pricingCapable: false,
  },
  {
    id: "loading_dock",
    /** CRM name is the HU canonical product type (migration 149). Toló kapu stays for old deals. */
    productType: "Rakodástechnika",
    previewMode: "layer_stack",
    hasAutomation: false,
    pricingCapable: false,
  },
  {
    id: "high_speed",
    productType: "Gyors kapu",
    previewMode: "photo_stack",
    hasAutomation: true,
    pricingCapable: false,
  },
  {
    id: "barrier",
    productType: "Sorompó",
    previewMode: "layer_stack",
    hasAutomation: false,
    pricingCapable: false,
  },
];

export function getAccessCategory(id: string): AccessCategoryDef | undefined {
  return ACCESS_CATEGORIES.find((c) => c.id === id);
}

/** Default RAL-like palette for layered previews */
export const DEFAULT_ACCESS_COLORS: AccessColorOption[] = [
  { code: "RAL 9016", name: "Traffic white", colorHex: "#F6F6F6" },
  { code: "RAL 7016", name: "Anthracite grey", colorHex: "#383E42" },
  { code: "RAL 9005", name: "Jet black", colorHex: "#0A0A0A" },
  { code: "RAL 8017", name: "Chocolate brown", colorHex: "#45322E" },
  { code: "RAL 5010", name: "Gentian blue", colorHex: "#0E518D" },
  { code: "RAL 3000", name: "Flame red", colorHex: "#A72920" },
];

/**
 * Wiśniowski MakroPro industrial palette. Slovak `name` values are provisional
 * (from the layer pack) and need proofreading.
 * Hex: Wikipedia RAL sRGB, except 9016 = TPT `#f1f0ea`.
 */
export const INDUSTRIAL_ACCESS_COLORS: AccessColorOption[] = [
  { code: "RAL 9016", name: "RAL 9016 Biela", colorHex: "#f1f0ea" },
  { code: "RAL 9006", name: "RAL 9006 Biely hliník", colorHex: "#a5a8a6" },
  { code: "RAL 9007", name: "RAL 9007 Sivý hliník", colorHex: "#8f8f8c" },
  { code: "RAL 7016", name: "RAL 7016 Antracitová sivá", colorHex: "#373f43" },
  { code: "RAL 7035", name: "RAL 7035 Svetlo sivá", colorHex: "#cbd0cc" },
  { code: "RAL 5010", name: "RAL 5010 Enciánová modrá", colorHex: "#13447c" },
  { code: "RAL 3000", name: "RAL 3000 Ohnivo červená", colorHex: "#ab2524" },
  { code: "RAL 6005", name: "RAL 6005 Machovo zelená", colorHex: "#0f4336" },
  { code: "RAL 1021", name: "RAL 1021 Repková žltá", colorHex: "#f6b600" },
  { code: "RAL 8014", name: "RAL 8014 Sépiovo hnedá", colorHex: "#49392d" },
  { code: "RAL 9005", name: "RAL 9005 Čierna", colorHex: "#131516" },
];

/** Wiśniowski BR-100 roller palette. Slovak `name` values are provisional. */
export const ROLLER_ACCESS_COLORS: AccessColorOption[] = [
  { code: "RAL 9016", name: "RAL 9016 Biela", colorHex: "#f1f0ea" },
  { code: "RAL 9006", name: "RAL 9006 Biely hliník", colorHex: "#a5a8a6" },
  { code: "RAL 9007", name: "RAL 9007 Sivý hliník", colorHex: "#8f8f8c" },
  { code: "RAL 7016", name: "RAL 7016 Antracitová sivá", colorHex: "#373f43" },
  { code: "RAL 7012", name: "RAL 7012 Čadičovo sivá", colorHex: "#596163" },
  { code: "RAL 8014", name: "RAL 8014 Sépiovo hnedá", colorHex: "#49392d" },
  { code: "RAL 9005", name: "RAL 9005 Čierna", colorHex: "#131516" },
  { code: "RAL 5010", name: "RAL 5010 Enciánová modrá", colorHex: "#13447c" },
  { code: "RAL 3000", name: "RAL 3000 Ohnivo červená", colorHex: "#ab2524" },
  { code: "RAL 6005", name: "RAL 6005 Machovo zelená", colorHex: "#0f4336" },
];

/** Wiśniowski swing garage door infill. `h` is Vzor H (Connect). */
export type SwingPattern = "0" | "1" | "2" | "4" | "6" | "7" | "8" | "9" | "h";
/** `zn` galvanised frame (opaque). `rf` frame takes the leaf colour. */
export type SwingFrame = "zn" | "rf";
export type SwingLeaves = "single" | "double";
export type SwingFoil = "zlaty-dub" | "orech";
/** Window pack: none, A/H horizontal, A/V vertical (two panes per leaf). */
export type SwingGlazing = "none" | "ah" | "av";

export const SWING_PATTERNS: SwingPattern[] = ["0", "1", "2", "4", "6", "7", "8", "9", "h"];
export const SWING_FOILS: SwingFoil[] = ["zlaty-dub", "orech"];
export const SWING_GLAZINGS: SwingGlazing[] = ["none", "ah", "av"];
export const DEFAULT_SWING_GLAZING: SwingGlazing = "none";
/** Single leaf exists for these patterns only (6 and 7 are double-leaf). */
export const SWING_SINGLE_PATTERNS: SwingPattern[] = ["0", "1", "2", "4", "8", "9", "h"];
/** Patterns whose frame is leaf colour only. */
export const SWING_RF_ONLY_PATTERNS: SwingPattern[] = ["6", "7", "h"];
export const DEFAULT_SWING_PATTERN: SwingPattern = "0";
export const DEFAULT_SWING_FRAME: SwingFrame = "zn";

/** Double leaf max 5000×4000 mm, single leaf max 2500×4000 mm. */
export const SWING_DOUBLE_MAX_MM = { width: 5000, height: 4000 };
export const SWING_SINGLE_MAX_MM = { width: 2500, height: 4000 };

/**
 * Wiśniowski swing garage door palette (AVAILABLE STANDARD COLOURS).
 * Order matches the manufacturer chart. Foil swatches use wood-grain JPGs
 * (`swatch-zlaty-dub.jpg` / `swatch-orech.jpg`); RAL entries use `ralNNNN.jpg`.
 */
export const SWING_ACCESS_COLORS: AccessColorOption[] = [
  { code: "RAL 7016", name: "Grafitová RAL 7016", colorHex: "#373f43", overlayFilename: "ral7016.jpg" },
  { code: "RAL 7035", name: "Svetlosivá RAL 7035", colorHex: "#cbd0cc", overlayFilename: "ral7035.jpg" },
  { code: "RAL 8014", name: "Hnedá RAL 8014", colorHex: "#49392d", overlayFilename: "ral8014.jpg" },
  { code: "RAL 8017", name: "Tmavohnedá RAL 8017", colorHex: "#442f29", overlayFilename: "ral8017.jpg" },
  { code: "RAL 9005", name: "Čierna RAL 9005", colorHex: "#131516", overlayFilename: "ral9005.jpg" },
  { code: "RAL 9006", name: "Strieborná RAL 9006", colorHex: "#a5a8a6", overlayFilename: "ral9006.jpg" },
  { code: "RAL 9016", name: "Biela RAL 9016", colorHex: "#f1f0ea", overlayFilename: "ral9016.jpg" },
  { code: "zlaty-dub", name: "Zlatý dub", colorHex: "#9e6329", overlayFilename: "swatch-zlaty-dub.jpg" },
  { code: "orech", name: "Orech", colorHex: "#634029", overlayFilename: "swatch-orech.jpg" },
];

/** GIOVI Active Roll structure. Powder-coated frame is not rendered. */
export type HighSpeedFrame = "galv" | "inox";
/** none = no vision panel, win = one window row, band = one transparent section. */
export type HighSpeedGlazing = "none" | "win" | "band";
export const HIGH_SPEED_FRAMES: HighSpeedFrame[] = ["galv", "inox"];
export const HIGH_SPEED_GLAZINGS: HighSpeedGlazing[] = ["none", "win", "band"];
export const DEFAULT_HIGH_SPEED_FRAME: HighSpeedFrame = "galv";
export const DEFAULT_HIGH_SPEED_GLAZING: HighSpeedGlazing = "none";
/** Catalogue max (GV-ENG-Catalog R05 11/2024). Larger sizes need a feasibility check. */
export const HIGH_SPEED_CATALOGUE_MAX_MM = 6000;
/** giovidoors.com Active Roll page maximum. */
export const HIGH_SPEED_HARD_MAX_MM = { width: 8500, height: 8400 };

/**
 * GIOVI curtain colours from `high_speed/colors.json` (catalogue standard list,
 * plus RAL 9006 optional and RAL 9010 insulated-only). Hex = Wikipedia RAL sRGB.
 * English `name` values; the UI translates them.
 */
export const HIGH_SPEED_ACCESS_COLORS: AccessColorOption[] = [
  { code: "RAL 1003", name: "RAL 1003 Signal yellow", colorHex: "#f9a800" },
  { code: "RAL 1013", name: "RAL 1013 Oyster white", colorHex: "#e3d9c6" },
  { code: "RAL 1021", name: "RAL 1021 Colza yellow", colorHex: "#f6b600" },
  { code: "RAL 2004", name: "RAL 2004 Pure orange", colorHex: "#e75b12" },
  { code: "RAL 3002", name: "RAL 3002 Carmine red", colorHex: "#a1232b" },
  { code: "RAL 5002", name: "RAL 5002 Ultramarine blue", colorHex: "#2b2c7c" },
  { code: "RAL 5010", name: "RAL 5010 Gentian blue", colorHex: "#13447c" },
  { code: "RAL 5012", name: "RAL 5012 Light blue", colorHex: "#3481b8" },
  { code: "RAL 5013", name: "RAL 5013 Cobalt blue", colorHex: "#232d53" },
  { code: "RAL 5015", name: "RAL 5015 Sky blue", colorHex: "#2874b2" },
  { code: "RAL 5017", name: "RAL 5017 Traffic blue", colorHex: "#0e518d" },
  { code: "RAL 6001", name: "RAL 6001 Emerald green", colorHex: "#28713e" },
  { code: "RAL 6005", name: "RAL 6005 Moss green", colorHex: "#0f4336" },
  { code: "RAL 6018", name: "RAL 6018 Yellow green", colorHex: "#48a43f" },
  { code: "RAL 6026", name: "RAL 6026 Opal green", colorHex: "#005d52" },
  { code: "RAL 7016", name: "RAL 7016 Anthracite grey", colorHex: "#373f43" },
  { code: "RAL 7035", name: "RAL 7035 Light grey", colorHex: "#cbd0cc" },
  { code: "RAL 7036", name: "RAL 7036 Platinum grey", colorHex: "#9a9697" },
  { code: "RAL 7037", name: "RAL 7037 Dusty grey", colorHex: "#7c7f7e" },
  { code: "RAL 7038", name: "RAL 7038 Agate grey", colorHex: "#b4b8b0" },
  { code: "RAL 8017", name: "RAL 8017 Chocolate brown", colorHex: "#44322d" },
  { code: "RAL 9003", name: "RAL 9003 Signal white", colorHex: "#ffffff" },
  { code: "RAL 9005", name: "RAL 9005 Jet black", colorHex: "#131516" },
  { code: "RAL 9006", name: "RAL 9006 White aluminium", colorHex: "#a5a8a6" },
  { code: "RAL 9010", name: "RAL 9010 Pure white", colorHex: "#f7f9ef" },
];

export type BarrierModel = "way004" | "way005" | "way006" | "bionik4" | "bionik4hp" | "bionik6" | "bionik8";
/** `inox` is the Roger "/IS" brushed stainless cabinet (BIONIK 4, 4HP, 6 only). */
export type BarrierFinish = "ral9006" | "inox";
export type BarrierSupport = "none" | "fixed" | "mobile";

export const BARRIER_MODELS: BarrierModel[] = [
  "way004",
  "way005",
  "way006",
  "bionik4",
  "bionik4hp",
  "bionik6",
  "bionik8",
];
export const DEFAULT_BARRIER_MODEL: BarrierModel = "way004";
export const DEFAULT_BARRIER_FINISH: BarrierFinish = "ral9006";
export const DEFAULT_BARRIER_SUPPORT: BarrierSupport = "none";
export const BARRIER_BOOM_DEFAULT_MM = 4000;
export const BARRIER_BOOM_MAX_MM = 8000;

/** Drawn boom lengths (metres) shipped as PNGs, and whether an /IS render exists. */
export const BARRIER_PREVIEW: Record<BarrierModel, { lengths: number[]; inox: boolean }> = {
  way004: { lengths: [3, 4], inox: false },
  way005: { lengths: [5], inox: false },
  way006: { lengths: [6], inox: false },
  bionik4: { lengths: [3, 4], inox: true },
  bionik4hp: { lengths: [4], inox: true },
  bionik6: { lengths: [5, 6], inox: true },
  bionik8: { lengths: [7, 8], inox: false },
};

export interface AccessPhotoCatalog {
  industrial: {
    motifs: IndustrialMotif[];
    surfaces: IndustrialSurface[];
    colors: AccessColorOption[];
  };
  roller: {
    cassettes: RollerCassette[];
    colors: AccessColorOption[];
  };
  swing: {
    patterns: SwingPattern[];
    frames: SwingFrame[];
    colors: AccessColorOption[];
  };
  high_speed: {
    frames: HighSpeedFrame[];
    glazings: HighSpeedGlazing[];
    colors: AccessColorOption[];
  };
  barrier: {
    models: BarrierModel[];
    supports: BarrierSupport[];
  };
}

/** Fixed by the shipped JPEG/PNG layers — not the shared colour list. */
export const ACCESS_PHOTO_CATALOG: AccessPhotoCatalog = {
  industrial: {
    motifs: INDUSTRIAL_MOTIFS,
    surfaces: INDUSTRIAL_SURFACES,
    colors: INDUSTRIAL_ACCESS_COLORS,
  },
  roller: {
    cassettes: ROLLER_CASSETTES,
    colors: ROLLER_ACCESS_COLORS,
  },
  swing: {
    patterns: SWING_PATTERNS,
    frames: ["zn", "rf"],
    colors: SWING_ACCESS_COLORS,
  },
  high_speed: {
    frames: HIGH_SPEED_FRAMES,
    glazings: HIGH_SPEED_GLAZINGS,
    colors: HIGH_SPEED_ACCESS_COLORS,
  },
  barrier: {
    models: BARRIER_MODELS,
    supports: ["none", "fixed", "mobile"],
  },
};

export interface AccessRogerSeriesRule {
  id: string;
  /** Category where this rule applies. `"sliding"` is legacy and hidden on display. */
  categories: StoredAccessCategoryId[];
  series: string;
  label: string;
  /** Max leaf/boom length in meters (optional) */
  maxLengthM?: number;
  /** Max weight kg (optional) */
  maxWeightKg?: number;
  mounts?: AccessAutomationMount[];
  leafCount?: (1 | 2)[];
  /** Barrier preview image family. Absent on rules saved before the model sheet. */
  previewModel?: BarrierModel;
}

/** MVP Roger Technology series recommendations (not full SKU catalogue). Swing garage doors and loading technology have no operator block. */
export const DEFAULT_ACCESS_ROGER_RULES: AccessRogerSeriesRule[] = [
  {
    id: "residential-ul",
    categories: ["residential_garage"],
    series: "UL/1400",
    label: "UL/1400 — sectional operator",
    mounts: ["surface"],
  },
  {
    id: "residential-c40",
    categories: ["residential_garage"],
    series: "C40",
    label: "C40 — sectional chain motor",
    mounts: ["surface"],
  },
  {
    id: "barrier-way004",
    categories: ["barrier"],
    series: "WAY/004",
    label: "WAY/004 — 24V brushless, boom up to 4 m",
    maxLengthM: 4,
    previewModel: "way004",
  },
  {
    id: "barrier-bionik4",
    categories: ["barrier"],
    series: "BIONIK 4",
    label: "BIONIK 4 (BI/004) — 36V brushless, boom up to 4 m",
    maxLengthM: 4,
    previewModel: "bionik4",
  },
  {
    id: "barrier-bionik4hp",
    categories: ["barrier"],
    series: "BIONIK 4HP",
    label: "BIONIK 4HP (BI/004HP) — super-intensive, boom up to 4 m",
    maxLengthM: 4,
    previewModel: "bionik4hp",
  },
  {
    id: "barrier-way005",
    categories: ["barrier"],
    series: "WAY/005",
    label: "WAY/005 — 24V brushless, boom up to 5 m",
    maxLengthM: 5,
    previewModel: "way005",
  },
  {
    id: "barrier-way006",
    categories: ["barrier"],
    series: "WAY/006",
    label: "WAY/006 — 24V brushless, boom up to 6 m",
    maxLengthM: 6,
    previewModel: "way006",
  },
  {
    id: "barrier-bionik6",
    categories: ["barrier"],
    series: "BIONIK 6",
    label: "BIONIK 6 (BI/006) — super-intensive, boom up to 6 m",
    maxLengthM: 6,
    previewModel: "bionik6",
  },
  {
    id: "barrier-bionik8",
    categories: ["barrier"],
    series: "BIONIK 8",
    label: "BIONIK 8 (BI/008) — super-intensive, boom up to 8 m",
    maxLengthM: 8,
    previewModel: "bionik8",
  },
];

export interface AccessRogerSuggestInput {
  categoryId: AccessCategoryId;
  lengthM?: number;
  weightKg?: number;
  mount?: AccessAutomationMount;
  leafCount?: 1 | 2;
}

export function suggestRogerSeries(
  input: AccessRogerSuggestInput,
  rules: AccessRogerSeriesRule[] = DEFAULT_ACCESS_ROGER_RULES
): AccessRogerSeriesRule[] {
  return rules.filter((r) => {
    if (!r.categories.includes(input.categoryId)) return false;
    if (r.maxLengthM != null && input.lengthM != null && input.lengthM > r.maxLengthM) return false;
    if (r.maxWeightKg != null && input.weightKg != null && input.weightKg > r.maxWeightKg) return false;
    if (r.mounts && input.mount && !r.mounts.includes(input.mount)) return false;
    if (r.leafCount && input.leafCount && !r.leafCount.includes(input.leafCount)) return false;
    return true;
  }).sort((a, b) => {
    if (input.categoryId !== "barrier") return 0;
    return (a.maxLengthM ?? 99) - (b.maxLengthM ?? 99);
  });
}

function ruleCategories(rule: AccessRogerSeriesRule): StoredAccessCategoryId[] {
  return Array.isArray(rule.categories) ? rule.categories : [];
}

/** Rules that applied only to the removed sliding category. Hidden on display; stored JSON is left as-is. */
export function isSlidingOnlyRogerRule(rule: AccessRogerSeriesRule): boolean {
  const cats = ruleCategories(rule);
  return cats.length > 0 && cats.every((c) => c === "sliding");
}

/**
 * Saved settings stay valid. Barrier rules written before `previewModel` existed
 * are replaced, for display only, by the built-in model catalogue. Sliding-only
 * rules (M30 / H30 / R30) are dropped from the display. Other saved rules stay.
 * The stored JSON is not rewritten until an admin resets and saves.
 */
export function resolveAccessRogerRules(
  saved?: AccessRogerSeriesRule[] | null
): AccessRogerSeriesRule[] {
  if (!saved || saved.length === 0) return DEFAULT_ACCESS_ROGER_RULES;
  const defaultBarrier = DEFAULT_ACCESS_ROGER_RULES.filter((r) => r.categories.includes("barrier"));
  const savedBarrier = saved.filter((r) => ruleCategories(r).includes("barrier"));
  const savedOther = saved.filter((r) => !ruleCategories(r).includes("barrier") && !isSlidingOnlyRogerRule(r));
  const barrier = savedBarrier.some((r) => !!r.previewModel) ? savedBarrier : defaultBarrier;
  return [...savedOther, ...barrier];
}

const BARRIER_MODEL_SET = new Set<string>(BARRIER_MODELS);

/** Map a stored Roger rule onto a preview image. Old "WAY" / "BIONIK 4" rows still resolve. */
export function barrierModelFromRule(rule: AccessRogerSeriesRule): BarrierModel | null {
  if (rule.previewModel && BARRIER_MODEL_SET.has(rule.previewModel)) return rule.previewModel;
  const series = rule.series.toUpperCase().replace(/\s+/g, " ").trim();
  if (series.includes("4HP") || series.includes("004HP")) return "bionik4hp";
  if (series.includes("BIONIK 8") || series.includes("BI/008") || series.includes("BIONIK8")) return "bionik8";
  if (series.includes("BIONIK 6") || series.includes("BI/006") || series.includes("BIONIK6")) return "bionik6";
  if (series.includes("BIONIK 4") || series.includes("BI/004") || series.includes("BIONIK4")) return "bionik4";
  if (series.includes("WAY") && series.includes("006")) return "way006";
  if (series.includes("WAY") && series.includes("005")) return "way005";
  if (series.includes("WAY") && series.includes("004")) return "way004";
  if (series === "WAY" || series.startsWith("WAY ")) {
    const max = rule.maxLengthM ?? 5;
    if (max <= 4) return "way004";
    if (max <= 5) return "way005";
    return "way006";
  }
  return null;
}

export interface AccessConfiguratorOptions {
  /** Enabled category ids; empty/undefined = all. `"sliding"` is read as `loading_dock`. */
  enabledCategories?: StoredAccessCategoryId[];
  colors?: AccessColorOption[];
  /** Editable industrial + roller surfaces/colours (admin palette tab). */
  photoCatalog?: AccessPhotoCatalogStored;
}

function normalizeStoredColor(raw: unknown): AccessColorOption | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const code = typeof o.code === "string" ? o.code.trim() : "";
  if (!code) return null;
  const colorHex = typeof o.colorHex === "string" && o.colorHex.trim() ? o.colorHex.trim() : "#cccccc";
  const name = localizeName(o.name as string | AccessLocalizedName | undefined, code);
  const overlayFilename =
    typeof o.overlayFilename === "string" && o.overlayFilename.trim()
      ? o.overlayFilename.trim()
      : undefined;
  return { code, name, colorHex, overlayFilename };
}

function normalizeNamedOption(raw: unknown): AccessNamedOption | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id.trim() : "";
  if (!id) return null;
  const baseFilename =
    typeof o.baseFilename === "string" && o.baseFilename.trim()
      ? o.baseFilename.trim()
      : undefined;
  return { id, name: localizeName(o.name as string | AccessLocalizedName | undefined, id), baseFilename };
}

function normalizeSurfaceGroup(raw: unknown): AccessSurfaceGroup | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === "string" ? o.id.trim() : "";
  if (!id) return null;
  const colors = Array.isArray(o.colors)
    ? (o.colors.map(normalizeStoredColor).filter(Boolean) as AccessColorOption[])
    : [];
  return {
    id,
    name: localizeName(o.name as string | AccessLocalizedName | undefined, id),
    colors,
  };
}

/** Default industrial + roller catalogue matching today's hard-coded RAL lists + JPEG names. */
export function buildDefaultAccessPhotoCatalog(): AccessPhotoCatalogStored {
  return {
    industrial: {
      motifs: INDUSTRIAL_MOTIFS.map((id) => ({
        id,
        name: localizeName(id),
        baseFilename: `base-${id}.png`,
      })),
      surfaces: INDUSTRIAL_SURFACES.map((id) => ({
        id,
        name: localizeName(id),
        colors: INDUSTRIAL_ACCESS_COLORS.map((c) => {
          const ral = normalizeRalCode(c.code);
          return {
            ...c,
            name: localizeName(c.name, c.code),
            overlayFilename: ral ? `${id}_ral${ral}.jpg` : undefined,
          };
        }),
      })),
    },
    roller: {
      cassettes: ROLLER_CASSETTES.map((id) => ({
        id,
        name: localizeName(id),
        baseFilename: `base-${id}.png`,
      })),
      colors: ROLLER_ACCESS_COLORS.map((c) => {
        const ral = normalizeRalCode(c.code);
        return {
          ...c,
          name: localizeName(c.name, c.code),
          overlayFilename: ral ? `ral${ral}.jpg` : undefined,
        };
      }),
    },
  };
}

/** Resolve stored photoCatalog over defaults (empty arrays fall back to default). */
export function resolveAccessPhotoCatalog(
  options?: AccessConfiguratorOptions | null
): AccessPhotoCatalogStored {
  const defaults = buildDefaultAccessPhotoCatalog();
  const stored = options?.photoCatalog;
  if (!stored || typeof stored !== "object") return defaults;

  const motifs = Array.isArray(stored.industrial?.motifs)
    ? (stored.industrial.motifs.map(normalizeNamedOption).filter(Boolean) as AccessNamedOption[])
    : [];
  const surfaces = Array.isArray(stored.industrial?.surfaces)
    ? (stored.industrial.surfaces.map(normalizeSurfaceGroup).filter(Boolean) as AccessSurfaceGroup[])
    : [];
  const cassettes = Array.isArray(stored.roller?.cassettes)
    ? (stored.roller.cassettes.map(normalizeNamedOption).filter(Boolean) as AccessNamedOption[])
    : [];
  const rollerColors = Array.isArray(stored.roller?.colors)
    ? (stored.roller.colors.map(normalizeStoredColor).filter(Boolean) as AccessColorOption[])
    : [];

  return {
    industrial: {
      motifs: motifs.length > 0 ? motifs : defaults.industrial.motifs,
      surfaces: surfaces.length > 0 ? surfaces : defaults.industrial.surfaces,
    },
    roller: {
      cassettes: cassettes.length > 0 ? cassettes : defaults.roller.cassettes,
      colors: rollerColors.length > 0 ? rollerColors : defaults.roller.colors,
    },
  };
}

export function resolveIndustrialSurfaceColors(
  catalog: AccessPhotoCatalogStored,
  surfaceId?: string | null
): AccessColorOption[] {
  const surfaces = catalog.industrial.surfaces;
  if (surfaces.length === 0) return INDUSTRIAL_ACCESS_COLORS;
  const surface =
    (surfaceId && surfaces.find((s) => s.id === surfaceId)) ||
    surfaces.find((s) => s.id === DEFAULT_INDUSTRIAL_SURFACE) ||
    surfaces[0];
  return surface.colors.length > 0 ? surface.colors : INDUSTRIAL_ACCESS_COLORS;
}

export function resolveAccessColors(
  options?: AccessConfiguratorOptions | null,
  categoryId?: AccessCategoryId,
  surfaceId?: string | null
): AccessColorOption[] {
  if (categoryId === "industrial") {
    return resolveIndustrialSurfaceColors(resolveAccessPhotoCatalog(options), surfaceId);
  }
  if (categoryId === "roller") {
    const colors = resolveAccessPhotoCatalog(options).roller.colors;
    return colors.length > 0 ? colors : ROLLER_ACCESS_COLORS;
  }
  if (categoryId === "swing") return SWING_ACCESS_COLORS;
  if (categoryId === "high_speed") return HIGH_SPEED_ACCESS_COLORS;
  if (categoryId === "loading_dock" || categoryId === "barrier") return [];
  if (options?.colors && options.colors.length > 0) return options.colors;
  return DEFAULT_ACCESS_COLORS;
}

/** Map a stored category id onto a category the picker still offers. */
export function normalizeStoredCategoryId(id: string): AccessCategoryId | null {
  const mapped = id === "sliding" ? "loading_dock" : id;
  return ACCESS_CATEGORIES.some((c) => c.id === mapped) ? (mapped as AccessCategoryId) : null;
}

export function resolveEnabledCategories(
  options?: AccessConfiguratorOptions | null
): AccessCategoryDef[] {
  const enabled = options?.enabledCategories;
  if (!enabled || enabled.length === 0) return ACCESS_CATEGORIES;
  const ids = new Set<AccessCategoryId>();
  for (const raw of enabled) {
    const id = normalizeStoredCategoryId(String(raw));
    if (id) ids.add(id);
  }
  return ACCESS_CATEGORIES.filter((c) => ids.has(c.id));
}

/** Asset base path under public/ */
export const ACCESS_ASSET_BASE = "/access-configurator";

/**
 * Bump when replacing unhashed swing glazing PNGs under `public/…/overlays/`.
 * Query string bypasses stale PWA/Workbox precache entries for the same path.
 */
export const ACCESS_SWING_GLAZING_ASSET_V = "83271a2";

export function accessSwingGlazingSwatchPath(id: SwingGlazing): string {
  return `${ACCESS_ASSET_BASE}/swing/overlays/swatch-glazing-${id}.png?v=${ACCESS_SWING_GLAZING_ASSET_V}`;
}

export function accessSwingGlazingLayerPath(leaves: SwingLeaves, glazing: Exclude<SwingGlazing, "none">): string {
  return `${ACCESS_ASSET_BASE}/swing/overlays/glazing-${leaves}-${glazing}.png?v=${ACCESS_SWING_GLAZING_ASSET_V}`;
}

export function accessCategoryThumbPath(id: AccessCategoryId): string {
  return `${ACCESS_ASSET_BASE}/categories/${id}.png`;
}

/** SVG fallback when a realistic PNG thumb is missing */
export function accessCategoryThumbSvgPath(id: AccessCategoryId): string {
  return `${ACCESS_ASSET_BASE}/categories/${id}.svg`;
}

export function accessPreviewAssetPath(
  categoryId: AccessCategoryId,
  kind: "base" | "fill" | "variant",
  variant?: string
): string {
  const folder =
    categoryId === "high_speed"
      ? "high-speed"
      : categoryId === "residential_garage"
        ? "residential"
        : categoryId === "loading_dock"
          ? "loading-dock"
          : categoryId;
  if (kind === "variant") {
    return `${ACCESS_ASSET_BASE}/${folder}/variant-${variant ?? "a"}.svg`;
  }
  if (kind === "base") {
    return `${ACCESS_ASSET_BASE}/${folder}/base-${variant ?? "default"}.svg`;
  }
  return `${ACCESS_ASSET_BASE}/${folder}/fill.svg`;
}

export interface AccessPhotoStackPaths {
  /** Colour/texture JPEG, bottom layer */
  overlay: string;
  overlay2x: string;
  /** Transparent RGBA base PNG (panel lines, frame, shadow), mid layer */
  base: string;
  base2x: string;
  /** Optional top detail (e.g. swing window pack) */
  detail?: string | null;
  detail2x?: string | null;
}

/** Digits of a RAL code (`RAL 9016`, `9016`). */
export function normalizeRalCode(code: string | undefined | null): string | null {
  if (!code) return null;
  const match = String(code).match(/(\d{4})/);
  return match ? match[1] : null;
}

function withRetina(url: string): string {
  return url.replace(/(\.(?:png|jpe?g))(\?[^#]*)?(#.*)?$/i, "@2x$1$2$3");
}

/**
 * Photo-stack asset URLs (industrial, roller, swing garage door, high-speed).
 * Overlay and base share a pixel size (600×450, `@2x` 1200×900).
 * Returns null for categories that still use the SVG wash or a layer stack (barrier, loading dock).
 */
export function isSwingPattern(value: string | null | undefined): value is SwingPattern {
  return !!value && (SWING_PATTERNS as string[]).includes(value);
}

export function isSwingFoil(value: string | null | undefined): value is SwingFoil {
  const v = (value ?? "").toLowerCase();
  return v === "zlaty-dub" || v === "orech";
}

export function isSwingGlazing(value: string | null | undefined): value is SwingGlazing {
  return value === "none" || value === "ah" || value === "av";
}

export function swingPatternAllowsSingle(pattern: SwingPattern): boolean {
  return (SWING_SINGLE_PATTERNS as string[]).includes(pattern);
}

export function swingPatternAllowsZinc(pattern: SwingPattern): boolean {
  return !(SWING_RF_ONLY_PATTERNS as string[]).includes(pattern);
}

export interface SwingSelection {
  leaves: SwingLeaves;
  pattern: SwingPattern;
  frame: SwingFrame;
  /** `9016` or a foil id (`zlaty-dub` / `orech`). */
  finish: string;
}

/**
 * Drop combinations the e-shop does not sell: single leaf on patterns 6/7,
 * zinc frame on 6/7/H, RAL 9006 on H. The control the user just changed wins
 * when both cannot stay.
 */
export function normalizeSwingSelection(
  input: SwingSelection,
  changed?: "leaves" | "pattern" | "frame" | "finish"
): SwingSelection {
  let { leaves, pattern, frame } = input;
  const foil = isSwingFoil(input.finish);
  let finish = foil ? input.finish.toLowerCase() : (normalizeRalCode(input.finish) ?? "9016");

  if (changed === "leaves" && leaves === "single" && !swingPatternAllowsSingle(pattern)) {
    pattern = DEFAULT_SWING_PATTERN;
  }
  if (!swingPatternAllowsSingle(pattern)) leaves = "double";
  if (!swingPatternAllowsZinc(pattern) || (changed === "frame" && frame === "zn" && !swingPatternAllowsZinc(pattern))) {
    frame = "rf";
  }
  if (!foil && pattern === "h" && finish === "9006") finish = "9016";
  if (!foil && !SWING_ACCESS_COLORS.some((c) => normalizeRalCode(c.code) === finish)) finish = "9016";
  return { leaves, pattern, frame, finish };
}

export function swingPaletteCode(finish: string): string {
  if (isSwingFoil(finish)) return finish.toLowerCase();
  const ral = normalizeRalCode(finish);
  return ral ? `RAL ${ral}` : "RAL 9016";
}

export function swingColorsForPattern(pattern: SwingPattern): AccessColorOption[] {
  if (pattern !== "h") return SWING_ACCESS_COLORS;
  return SWING_ACCESS_COLORS.filter((c) => normalizeRalCode(c.code) !== "9006");
}

export function clampSwingSize(leaves: SwingLeaves, width: number, height: number): { width: number; height: number } {
  const max = leaves === "single" ? SWING_SINGLE_MAX_MM : SWING_DOUBLE_MAX_MM;
  return {
    width: Math.min(Math.max(Number.isFinite(width) ? width : 0, 0), max.width),
    height: Math.min(Math.max(Number.isFinite(height) ? height : 0, 0), max.height),
  };
}

export function clampHighSpeedSize(width: number, height: number): { width: number; height: number } {
  return {
    width: Math.min(Math.max(Number.isFinite(width) ? width : 0, 0), HIGH_SPEED_HARD_MAX_MM.width),
    height: Math.min(Math.max(Number.isFinite(height) ? height : 0, 0), HIGH_SPEED_HARD_MAX_MM.height),
  };
}

export function highSpeedExceedsCatalogue(width: number, height: number): boolean {
  return width > HIGH_SPEED_CATALOGUE_MAX_MM || height > HIGH_SPEED_CATALOGUE_MAX_MM;
}

export function clampBarrierBoom(lengthMm: number): number {
  const n = Number.isFinite(lengthMm) ? lengthMm : BARRIER_BOOM_DEFAULT_MM;
  return Math.min(Math.max(n, 0), BARRIER_BOOM_MAX_MM);
}

export function accessPhotoStackPaths(
  input: {
    categoryId: AccessCategoryId;
    colorCode?: string | null;
    motif?: string | null;
    surface?: string | null;
    cassette?: string | null;
    gateVariant?: string | null;
    swingPattern?: string | null;
    swingFrame?: string | null;
    swingLeaves?: string | null;
    swingGlazing?: string | null;
    frame?: string | null;
    glazing?: string | null;
  },
  catalog?: AccessPhotoCatalogStored | null
): AccessPhotoStackPaths | null {
  const ral = normalizeRalCode(input.colorCode) ?? "9016";
  if (input.categoryId === "swing") {
    const leaves: SwingLeaves =
      input.swingLeaves === "single" || input.gateVariant === "single_leaf" ? "single" : "double";
    const pattern = isSwingPattern(input.swingPattern) ? input.swingPattern : DEFAULT_SWING_PATTERN;
    const frame: SwingFrame = input.swingFrame === "rf" ? "rf" : DEFAULT_SWING_FRAME;
    const sel = normalizeSwingSelection({
      leaves,
      pattern,
      frame,
      finish: input.colorCode ?? "9016",
    });
    const base = `${ACCESS_ASSET_BASE}/swing/base-${sel.leaves}-${sel.pattern}-${sel.frame}.png`;
    const overlay = isSwingFoil(sel.finish)
      ? `${ACCESS_ASSET_BASE}/swing/overlays/${sel.leaves}-${sel.pattern}_${sel.finish}.jpg`
      : `${ACCESS_ASSET_BASE}/swing/overlays/ral${sel.finish}.jpg`;
    const glazing: SwingGlazing = isSwingGlazing(input.swingGlazing) ? input.swingGlazing : DEFAULT_SWING_GLAZING;
    const detail =
      glazing === "none" ? null : accessSwingGlazingLayerPath(sel.leaves, glazing);
    return {
      base,
      base2x: withRetina(base),
      overlay,
      overlay2x: withRetina(overlay),
      detail,
      detail2x: detail ? withRetina(detail) : null,
    };
  }
  if (input.categoryId === "high_speed") {
    const frame: HighSpeedFrame = input.frame === "inox" ? "inox" : DEFAULT_HIGH_SPEED_FRAME;
    const glazing: HighSpeedGlazing =
      input.glazing === "win" || input.glazing === "band" ? input.glazing : DEFAULT_HIGH_SPEED_GLAZING;
    const known = HIGH_SPEED_ACCESS_COLORS.some((c) => normalizeRalCode(c.code) === ral);
    const code = known ? ral : "1003";
    const base = `${ACCESS_ASSET_BASE}/high-speed/base-${frame}-${glazing}.png`;
    const overlay = `${ACCESS_ASSET_BASE}/high-speed/overlays/ral${code}.jpg`;
    return { base, base2x: withRetina(base), overlay, overlay2x: withRetina(overlay) };
  }
  if (input.categoryId === "industrial") {
    const resolved = catalog ?? buildDefaultAccessPhotoCatalog();
    const motifId =
      (input.motif && resolved.industrial.motifs.some((m) => m.id === input.motif)
        ? input.motif
        : null) ||
      (input.motif === "n" || input.motif === "w" || input.motif === "g"
        ? input.motif
        : DEFAULT_INDUSTRIAL_MOTIF);
    const surfaceId =
      (input.surface && resolved.industrial.surfaces.some((s) => s.id === input.surface)
        ? input.surface
        : null) ||
      resolved.industrial.surfaces[0]?.id ||
      DEFAULT_INDUSTRIAL_SURFACE;
    const surfaceColors = resolveIndustrialSurfaceColors(resolved, surfaceId);
    const known = surfaceColors.some((c) => normalizeRalCode(c.code) === ral);
    const code = known ? ral : normalizeRalCode(surfaceColors[0]?.code) || "9016";
    const colorOpt =
      surfaceColors.find((c) => normalizeRalCode(c.code) === code) ??
      surfaceColors.find((c) => normalizeRalCode(c.code) === ral);
    const motifOpt = resolved.industrial.motifs.find((m) => m.id === motifId);
    const baseFile = motifOpt?.baseFilename?.trim() || `base-${motifId}.png`;
    const overlayFile =
      colorOpt?.overlayFilename?.trim() || `${surfaceId}_ral${code}.jpg`;
    const base = `${ACCESS_ASSET_BASE}/industrial/${baseFile}`;
    const overlay = `${ACCESS_ASSET_BASE}/industrial/overlays/${overlayFile}`;
    return { base, base2x: withRetina(base), overlay, overlay2x: withRetina(overlay) };
  }
  if (input.categoryId === "roller") {
    const resolved = catalog ?? buildDefaultAccessPhotoCatalog();
    const cassetteId =
      (input.cassette && resolved.roller.cassettes.some((c) => c.id === input.cassette)
        ? input.cassette
        : null) ||
      (input.cassette === "nobox" || input.cassette === "box"
        ? input.cassette
        : DEFAULT_ROLLER_CASSETTE);
    const rollerColors =
      resolved.roller.colors.length > 0 ? resolved.roller.colors : ROLLER_ACCESS_COLORS;
    const known = rollerColors.some((c) => normalizeRalCode(c.code) === ral);
    const code = known ? ral : normalizeRalCode(rollerColors[0]?.code) || "9016";
    const colorOpt =
      rollerColors.find((c) => normalizeRalCode(c.code) === code) ??
      rollerColors.find((c) => normalizeRalCode(c.code) === ral);
    const cassetteOpt = resolved.roller.cassettes.find((c) => c.id === cassetteId);
    const baseFile = cassetteOpt?.baseFilename?.trim() || `base-${cassetteId}.png`;
    const overlayFile = colorOpt?.overlayFilename?.trim() || `ral${code}.jpg`;
    const base = `${ACCESS_ASSET_BASE}/roller/${baseFile}`;
    const overlay = `${ACCESS_ASSET_BASE}/roller/overlays/${overlayFile}`;
    return { base, base2x: withRetina(base), overlay, overlay2x: withRetina(overlay) };
  }
  return null;
}

export interface AccessStackLayer {
  src: string;
  src2x: string;
}

function isBarrierModel(value: string | null | undefined): value is BarrierModel {
  return !!value && BARRIER_MODEL_SET.has(value);
}

/**
 * Roger barrier: one body PNG plus optional skirt, support and LED layers.
 * Stack order is body → skirt → support → LED. Boom length picks the nearest
 * shipped drawing that is at least as long (otherwise the longest drawing).
 */
export function accessBarrierLayers(input: {
  model?: string | null;
  finish?: string | null;
  lengthMm?: number | null;
  led?: boolean;
  skirt?: boolean;
  support?: string | null;
}): AccessStackLayer[] {
  const model: BarrierModel = isBarrierModel(input.model) ? input.model : DEFAULT_BARRIER_MODEL;
  const def = BARRIER_PREVIEW[model];
  const lengthM = clampBarrierBoom(input.lengthMm ?? BARRIER_BOOM_DEFAULT_MM) / 1000;
  const len = def.lengths.find((l) => l + 1e-9 >= lengthM) ?? def.lengths[def.lengths.length - 1];
  const inox = input.finish === "inox" && def.inox;
  const key = `${model}${inox ? "-is" : ""}_${len}m`;
  const support: BarrierSupport =
    input.support === "fixed" || input.support === "mobile" ? input.support : "none";
  const files = [
    `${key}.png`,
    ...(input.skirt ? [`${key}_skirt.png`] : []),
    ...(support !== "none" ? [`${key}_support-${support}.png`] : []),
    ...(input.led ? [`${key}_led.png`] : []),
  ];
  return files.map((file) => {
    const src = `${ACCESS_ASSET_BASE}/barrier/${file}`;
    return { src, src2x: withRetina(src) };
  });
}

/** Hörmann loading technology. CRM product type (HU canonical). */
export const LOADING_DOCK_PRODUCT_TYPE = "Rakodástechnika";

export type DockItemId = "leveller" | "seal" | "bumpers" | "door";
export type DockLevellerType = "advise" | "hls2" | "htl2";
export type DockSealType = "advise" | "dsl" | "das3" | "cushion";

export const DOCK_LEVELLER_TYPES: DockLevellerType[] = ["advise", "hls2", "htl2"];
export const DOCK_SEAL_TYPES: DockSealType[] = ["advise", "dsl", "das3", "cushion"];

export interface LoadingDockSelection {
  leveller: boolean;
  levellerType: DockLevellerType;
  seal: boolean;
  sealType: DockSealType;
  bumpers: boolean;
  door: boolean;
  /** Integer >= 1. */
  bayCount: number;
  note: string;
}

/** Slovak labels stored on the deal so the card is readable without a translation lookup. */
export const LOADING_DOCK_ITEM_LABELS: Record<DockItemId, string> = {
  leveller: "Mostík",
  seal: "Tesniaci límec",
  bumpers: "Nárazníky",
  door: "Priemyselná brána",
};

export const LOADING_DOCK_TYPE_LABELS: Record<DockLevellerType | DockSealType, string> = {
  advise: "Poradíme",
  hls2: "HLS 2",
  htl2: "HTL 2",
  dsl: "DSL",
  das3: "DAS 3",
  cushion: "DFH/DFC",
};

export function normalizeDockLevellerType(value: unknown): DockLevellerType {
  return value === "hls2" || value === "htl2" ? value : "advise";
}

export function normalizeDockSealType(value: unknown): DockSealType {
  return value === "dsl" || value === "das3" || value === "cushion" ? value : "advise";
}

/** Integer 1–99, or null when missing / not an integer. */
export function normalizeBayCount(value: unknown): number | null {
  if (value == null || value === "") return null;
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1 || n > 99) return null;
  return n;
}

function asFlag(value: unknown): boolean {
  return value === true || value === 1 || value === "1" || value === "true";
}

export function parseLoadingDockSelection(input: {
  leveller?: unknown;
  levellerType?: unknown;
  seal?: unknown;
  sealType?: unknown;
  bumpers?: unknown;
  door?: unknown;
  bayCount?: unknown;
  note?: unknown;
}): { ok: true; selection: LoadingDockSelection } | { ok: false; message: string } {
  const selection: LoadingDockSelection = {
    leveller: asFlag(input.leveller),
    levellerType: normalizeDockLevellerType(input.levellerType),
    seal: asFlag(input.seal),
    sealType: normalizeDockSealType(input.sealType),
    bumpers: asFlag(input.bumpers),
    door: asFlag(input.door),
    bayCount: normalizeBayCount(input.bayCount) ?? 0,
    note: typeof input.note === "string" ? input.note.trim() : "",
  };
  if (!selection.leveller && !selection.seal && !selection.bumpers && !selection.door) {
    return { ok: false, message: "at least one loading dock item required" };
  }
  if (selection.bayCount < 1) {
    return { ok: false, message: "bayCount must be an integer >= 1" };
  }
  return { ok: true, selection };
}

export interface LoadingDockLine {
  dockItem: DockItemId;
  /** Machine value. Null for bumpers and the door (no type choice). `"advise"` stays `"advise"`. */
  dockType: DockLevellerType | DockSealType | null;
  itemLabel: string;
  /** Display type. Bumpers use "pár". The door has none. */
  typeLabel: string | null;
}

export function loadingDockLines(
  selection: Pick<LoadingDockSelection, "leveller" | "levellerType" | "seal" | "sealType" | "bumpers" | "door">
): LoadingDockLine[] {
  const lines: LoadingDockLine[] = [];
  if (selection.leveller) {
    lines.push({
      dockItem: "leveller",
      dockType: selection.levellerType,
      itemLabel: LOADING_DOCK_ITEM_LABELS.leveller,
      typeLabel: LOADING_DOCK_TYPE_LABELS[selection.levellerType],
    });
  }
  if (selection.seal) {
    lines.push({
      dockItem: "seal",
      dockType: selection.sealType,
      itemLabel: LOADING_DOCK_ITEM_LABELS.seal,
      typeLabel: LOADING_DOCK_TYPE_LABELS[selection.sealType],
    });
  }
  if (selection.bumpers) {
    lines.push({
      dockItem: "bumpers",
      dockType: null,
      itemLabel: LOADING_DOCK_ITEM_LABELS.bumpers,
      typeLabel: "pár",
    });
  }
  if (selection.door) {
    lines.push({
      dockItem: "door",
      dockType: null,
      itemLabel: LOADING_DOCK_ITEM_LABELS.door,
      typeLabel: null,
    });
  }
  return lines;
}

export interface LoadingDockProductRow {
  id: string;
  productType: typeof LOADING_DOCK_PRODUCT_TYPE;
  /** CRM product_types.id when resolved at lead create */
  productTypeId?: string;
  index: number;
  widths: string[];
  heights: string[];
  /** Item name, shown on the deal-card subtitle. Not a panel-surface lookup. */
  surface: string;
  color: string;
  /** Type label (or "pár"). Not a panel-emboss lookup. */
  prelis: string;
  notes: string;
  accessCategory: "loading_dock";
  dockItem: DockItemId;
  dockItemLabel: string;
  dockType: DockLevellerType | DockSealType | null;
  dockTypeLabel: string;
  bayCount: number;
  automationWanted: false;
  rogerSeries: null;
  pricingMode: "lead_only";
  gateVariant: "standard";
  visualVariant: string;
}

export function buildLoadingDockProducts(
  selection: LoadingDockSelection,
  extraLines: string[] = []
): LoadingDockProductRow[] {
  const extras = extraLines.map((l) => l.trim()).filter(Boolean);
  return loadingDockLines(selection).map((line, index) => {
    const head = line.typeLabel ? `${line.itemLabel}: ${line.typeLabel}` : line.itemLabel;
    const notes = [
      head,
      `Nakladacie miesta: ${selection.bayCount}`,
      selection.note ? `Poznámka: ${selection.note}` : null,
      ...extras,
    ]
      .filter(Boolean)
      .join("\n");
    return {
      id: crypto.randomUUID(),
      productType: LOADING_DOCK_PRODUCT_TYPE,
      index: index + 1,
      widths: [],
      heights: [],
      surface: line.itemLabel,
      color: "",
      prelis: line.typeLabel ?? "",
      notes,
      accessCategory: "loading_dock",
      dockItem: line.dockItem,
      dockItemLabel: line.itemLabel,
      dockType: line.dockType,
      dockTypeLabel: line.typeLabel ?? "",
      bayCount: selection.bayCount,
      automationWanted: false,
      rogerSeries: null,
      pricingMode: "lead_only",
      gateVariant: "standard",
      visualVariant: line.dockItem,
    };
  });
}

export function loadingDockDealNote(selection: LoadingDockSelection, extraLines: string[] = []): string {
  const extras = extraLines.map((l) => l.trim()).filter(Boolean);
  const bullets = loadingDockLines(selection).map((line) =>
    line.typeLabel ? `- ${line.itemLabel}: ${line.typeLabel}` : `- ${line.itemLabel}`
  );
  return [
    `Access konfigurátor: ${LOADING_DOCK_PRODUCT_TYPE} (loading_dock)`,
    `Nakladacie miesta: ${selection.bayCount}`,
    ...bullets,
    selection.note ? `Poznámka: ${selection.note}` : null,
    ...extras,
    "Árazás: lead only",
  ]
    .filter(Boolean)
    .join("\n");
}

/**
 * Hörmann loading bay. Stack order is base → leveller → door → bumpers → seal.
 * "Poradíme" (`advise`) uses the HLS 2 and DSL drawings. Unticked items are omitted;
 * `base` is always present.
 *
 * Test: `base-bleed.png` is a 3× canvas (3600×2700) with the original 1200×900
 * composition centered; preview scales the base ×3 so the opening matches the
 * accessory layers (still 600×450 / 1200×900).
 */
export const LOADING_DOCK_BLEED_SCALE = 3;

export function accessLoadingDockLayers(input: {
  leveller?: boolean;
  levellerType?: string | null;
  door?: boolean;
  bumpers?: boolean;
  seal?: boolean;
  sealType?: string | null;
  /** When true (default for now), use the expanded bleed base. */
  useBleedBase?: boolean;
}): AccessStackLayer[] {
  const lt = input.levellerType === "htl2" ? "htl2" : "hls2";
  const st = input.sealType === "das3" || input.sealType === "cushion" ? input.sealType : "dsl";
  const useBleed = input.useBleedBase !== false;
  const baseFile = useBleed ? "base-bleed.png" : "base.png";
  const files = [
    baseFile,
    ...(input.leveller ? [`leveller-${lt}.png`] : []),
    ...(input.door ? ["door.png"] : []),
    ...(input.bumpers ? ["bumpers.png"] : []),
    ...(input.seal ? [`seal-${st}.png`] : []),
  ];
  return files.map((file) => {
    const src = `${ACCESS_ASSET_BASE}/loading-dock/${file}`;
    return { src, src2x: withRetina(src) };
  });
}
